/**
 * Service-worker registration with auto-update (demo #8).
 *
 * `registerType: 'autoUpdate'` makes a freshly deployed service worker take
 * control and reload the page on its own once it's detected. The gap that left
 * crews staring at a stale build was that an already-open PWA never *checked*
 * for a new worker — so here we poll `registration.update()` on an interval and
 * again whenever the tab/app regains focus. Combined with the no-cache headers
 * on index.html / sw.js (staticwebapp.config.json), a new release now reaches
 * devices without anyone hard-refreshing.
 */
import { registerSW } from 'virtual:pwa-register';

const UPDATE_INTERVAL_MS = 60 * 60 * 1000; // hourly
const MIN_CHECK_GAP_MS = 5 * 60 * 1000; // throttle on-demand checks

let swRegistration: ServiceWorkerRegistration | null = null;
let lastCheck = 0;

/**
 * Ask the service worker to look for a new deploy now (throttled). A fresh
 * worker activates + auto-reloads on its own (autoUpdate). Call this at safe
 * moments — e.g. on route navigation — so an actively-focused desktop tab picks
 * up a release in seconds instead of waiting for the hourly tick.
 */
export function checkForAppUpdate(): void {
  if (!swRegistration) return;
  const now = Date.now();
  if (now - lastCheck < MIN_CHECK_GAP_MS) return;
  lastCheck = now;
  void swRegistration.update();
}

export function registerPwa(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      swRegistration = registration;
      const check = () => {
        lastCheck = Date.now();
        void registration.update();
      };
      setInterval(check, UPDATE_INTERVAL_MS);
      // Returning to a backgrounded tab/app is the most likely moment a crew
      // member would benefit from picking up a new release — check then too.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
    },
  });
}
